/** Filler tokens the transcriber emits for grunts, breaths, and half-words. */
const FILLER_TOKENS = new Set(['um', 'uh', 'hmm', 'hm', 'ah', 'oh', 'er', 'mm', 'huh', 'mhm']);

/**
 * Phrases speech models fabricate from noise or near-silence. These are training-data
 * artifacts (video outros, caption credits, non-speech markers) and never a real
 * request here, so they are rejected regardless of how loud the audio was.
 */
const TRANSCRIPT_ARTIFACTS = [
  /^you\b[.!?]*$/,
  /^thanks? (?:you )?for watching/,
  /^thank you for watching/,
  /(?:please )?subscribe to (?:my|our) channel/,
  /^(?:please )?(?:like and )?subscribe\b/,
  /amara\.org/,
  /^(?:subtitles?|captions?|transcription|translated)\s+by\b/,
  /^\[[^\]]*\]$/,
  /^\([^)]*\)$/,
  /^[♪♫\s]+$/,
];

/** Audio evidence tiers derived from how long the mic was above the noise floor. */
export const AUDIO_EVIDENCE = {
  UNKNOWN: 'unknown',
  WEAK: 'weak',
  OK: 'ok',
  STRONG: 'strong',
};

/** Minimum loud audio for a turn to be treated as real speech at all. */
export const MIN_LOUD_MS = 120;
/** Loud audio long enough that a borderline transcript can be trusted. */
export const STRONG_LOUD_MS = 350;

const isFillerOnly = (text) => {
  const tokens = text
    .toLowerCase()
    .replace(/[.!?,…]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return true;
  return tokens.every((token) => FILLER_TOKENS.has(token));
};

const isTranscriptArtifact = (text) => {
  const normalised = text.toLowerCase().trim();
  return TRANSCRIPT_ARTIFACTS.some((pattern) => pattern.test(normalised));
};

/**
 * Grades how much the microphone actually heard during a turn. Without this a
 * hallucinated transcript from background noise is indistinguishable from speech,
 * because the text itself looks perfectly plausible.
 */
export function classifyAudioEvidence(audio) {
  if (!audio || !Number.isFinite(audio.loudMs)) return AUDIO_EVIDENCE.UNKNOWN;
  if (audio.loudMs >= STRONG_LOUD_MS) return AUDIO_EVIDENCE.STRONG;
  if (audio.loudMs >= MIN_LOUD_MS) return AUDIO_EVIDENCE.OK;
  return AUDIO_EVIDENCE.WEAK;
}

/**
 * Returns true when a finalized user transcript should start an assistant turn.
 *
 * Text alone is not enough: transcription models invent plausible sentences from
 * ambient noise, so a turn also has to be backed by near-field audio. Evidence of
 * `unknown` (metering unavailable) fails open to text-only checks.
 */
export function isMeaningfulUserSpeech(transcript, { bargeIn = false, audio = null } = {}) {
  const text = (transcript || '').trim();
  if (!text) return false;
  if (text.length < 2) return false;
  if (!/\p{L}/u.test(text)) return false;
  if (/^[\s.,!?\-–—…]+$/.test(text)) return false;
  if (isFillerOnly(text)) return false;

  const evidence = classifyAudioEvidence(audio);
  if (evidence === AUDIO_EVIDENCE.WEAK) return false;

  if (isTranscriptArtifact(text)) return false;

  // A turn that interrupts the assistant needs clear near-field audio, since a
  // false interruption costs the user the answer they were already hearing.
  if (bargeIn && evidence === AUDIO_EVIDENCE.OK && text.length < 3) return false;

  return true;
}
